import { Avatar, Card, Col, Form, Row, Select, Skeleton, Space, Typography } from "antd";
import { BranchesOutlined } from "@ant-design/icons";
import moment from "moment-timezone";
import { GetMicroserviceVersionsResponseVersionsTags, ListKubernetesResponseKuberneteses } from "@microtica/ms-kube-sdk";
import { DefaultOptionType } from "antd/lib/select";

interface DeployApplicationModalGeneralSectionProps {
    serviceVersion?: GetMicroserviceVersionsResponseVersionsTags;
    serviceVersions: GetMicroserviceVersionsResponseVersionsTags[];
    handleServiceVersionChange: ((value: any, option: DefaultOptionType | DefaultOptionType[]) => void);
    clusterSection?: {
        namespaceDisabled: boolean;
        kubernetesNamespaces: (ListKubernetesResponseKuberneteses & { namespaces: string[] })[];
        handleClusterChange: ((value: any, option: DefaultOptionType | DefaultOptionType[]) => void);
        handleNamespaceSnippetChange: ((value: any, option: DefaultOptionType | DefaultOptionType[]) => void);
    },
    fetchMoreData?: Function;
    loading?: boolean;
}

const DeployApplicationModalGeneralSection = ({
    serviceVersion,
    serviceVersions,
    handleServiceVersionChange,
    clusterSection,
    fetchMoreData,
    loading
}: DeployApplicationModalGeneralSectionProps) => {
    const { Text } = Typography;
    let lastScrollPosition = 0;
    // don't display these namespaces
    const UNAVAILABLE_NAMESPACES = ["kube-node-lease", "kube-public", "kube-system"];

    const handleAboutToReachBottom = () => {
        if (fetchMoreData) {
            fetchMoreData();
        }
    }

    const handleScrollUpdate = (values: any) => {
        const { scrollTop, scrollHeight, clientHeight } = values.target;
        const pad = 50; // 50px of the bottom
        const percentageOfContentScrolled = ((scrollTop + pad) / (scrollHeight - clientHeight));
        // percentageOfContentScrolled will be greater than 1 if we are about to reach the bottom
        if (percentageOfContentScrolled > 1 && scrollTop > lastScrollPosition) {
            handleAboutToReachBottom();
        }
        lastScrollPosition = scrollTop;
    }

    return (
        <Card>
            <Card.Meta
                title="Version"
                description="Configure the image tag to be deployed. Avoid using the 'latest' tag, use exact tag instead"
            />
            <br />
            <Form.Item
                key="version"
                name="version"
                required={true}
                rules={[
                    { required: true, message: `Please select version!` }
                ]}
                initialValue={serviceVersion ? serviceVersion.name : undefined}
            >
                <Select
                    onChange={handleServiceVersionChange}
                    onPopupScroll={handleScrollUpdate}
                    loading={loading}
                >
                    {
                        serviceVersions.map(serviceVersion => (
                            <Select.Option value={serviceVersion.name} key={serviceVersion.name} className="flex-align-center">
                                <Row justify="space-between" align="middle">
                                    <Col>
                                        <Space direction="vertical">
                                            <Space size={5}>
                                                <Text
                                                    style={{ maxWidth: 350 }}
                                                    ellipsis={{ tooltip: serviceVersion.commit?.message }}
                                                >
                                                    {serviceVersion.commit?.message}
                                                </Text>
                                                <Text className="link">
                                                    ({serviceVersion.name?.slice(0, 7)})
                                                </Text>
                                            </Space>
                                            <Space>
                                                <BranchesOutlined />
                                                {serviceVersion.commit?.name}
                                            </Space>
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space direction="vertical" align="end">
                                            <div>
                                                {moment(serviceVersion.date).fromNow()}
                                            </div>
                                            <Space>
                                                <Avatar src={serviceVersion.commit?.user?.avatar} style={{ width: "15px", height: "15px" }} />
                                                {serviceVersion.commit?.user?.name}
                                            </Space>
                                        </Space>
                                    </Col>
                                </Row>
                            </Select.Option>
                        ))
                    }
                    {loading &&
                        <Select.Option value="placeholder" key="placeholder" className="flex-align-center" disabled>
                            <Skeleton paragraph={{ rows: 1 }} />
                        </Select.Option>
                    }
                </Select>
            </Form.Item>
            {clusterSection ?
                <>
                    <br />
                    <Card.Meta
                        title="Destination"
                        description="Choose on which cluster and namespace to deploy this application."
                    />
                    <br />
                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item
                                key="clusterId"
                                name="clusterId"
                                label="Cluster"
                                required={true}
                                rules={[
                                    { required: true, message: `Please select cluster!` }
                                ]}
                            >
                                <Select onChange={clusterSection.handleClusterChange}>
                                    {
                                        clusterSection.kubernetesNamespaces.map(({ name, id }) => (
                                            <Select.Option value={id} key={id}>{name}</Select.Option>
                                        ))
                                    }
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                shouldUpdate={(prevValues, currentValues) => prevValues.clusterId !== currentValues.clusterId}
                            >
                                {({ getFieldValue }) =>
                                    <Form.Item
                                        key="namespace"
                                        name="namespace"
                                        label="Namespace"
                                        required={true}
                                        rules={[
                                            { required: true, message: `Please select namespace!` }
                                        ]}>
                                        {getFieldValue('clusterId') ? (
                                            <Select
                                                onChange={clusterSection.handleNamespaceSnippetChange}
                                                disabled={clusterSection.namespaceDisabled}
                                            >
                                                {
                                                    clusterSection.kubernetesNamespaces
                                                        .filter(({ id }) => id === getFieldValue('clusterId'))
                                                        .map(({ namespaces }) => (
                                                            namespaces
                                                                .filter(namespace => !UNAVAILABLE_NAMESPACES.includes(namespace))
                                                                .map(namespace => (
                                                                    <Select.Option value={namespace} key={namespace}>{namespace}</Select.Option>
                                                                ))
                                                        ))
                                                }
                                            </Select>
                                        ) : <Select disabled={true}></Select>
                                        }
                                    </Form.Item>
                                }
                            </Form.Item>
                        </Col>
                    </Row>
                </> :
                <></>
            }
        </Card>
    )
}

export default DeployApplicationModalGeneralSection;